import { CafDailyEffects, CafDashboardCoverage, CafDashboardThroughput } from '../../../api/cafCorpus';
import { HOLD_GROUPS, HoldGroupKey, holdGroup } from '../copy';
import { percent } from '../format';

export interface OutcomeCounts { done: number; archived: number; staff: number }

/** One UTC day on the "handled by AI" line. `pct: null` means a genuine gap --
 *  either the sparse `daily` map never had this UTC day, or it was recorded
 *  with every count at zero -- and is never rendered as a misleading 0%
 *  (controller ruling: filling wins over dropping, but a gap stays honest). */
export interface DailyPoint { date: string; handled: number; total: number; pct: number | null }
export interface ReasonRow { key: HoldGroupKey; label: string; count: number }
export type GapReason = 'no_examples' | 'approval_pending' | 'switched_off' | 'missing_vector' | 'needs_review' | 'coverage_unknown';
export interface GapRow { sender: string; lender: string | null; count: number; existingExamples: number | null; reason: GapReason }

export const toOutcomes = (throughput: CafDashboardThroughput): OutcomeCounts => ({
  done: throughput.effects?.completed?.distinct_tasks ?? 0,
  archived: throughput.effects?.archived?.distinct_tasks ?? 0,
  // Attached but left open still needs a person, so it counts as Marked as manual.
  staff: (throughput.effects?.held?.distinct_tasks ?? 0) + (throughput.effects?.assigned?.distinct_tasks ?? 0),
});

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** 'YYYY-MM-DD' -> UTC midnight, ms. Pure string split + Date.UTC -- never
 *  `new Date('YYYY-MM-DD')` with local getters, which shifts a day under BST. */
const dayToUtcMs = (day: string): number => {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year, month - 1, date);
};

const utcMsToDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Every UTC calendar day from `from` to `to` inclusive, ascending. */
const utcDayRange = (from: string, to: string): string[] => {
  const days: string[] = [];
  for (let ms = dayToUtcMs(from); ms <= dayToUtcMs(to); ms += ONE_DAY_MS) days.push(utcMsToDay(ms));
  return days;
};

/**
 * Daily effects are sparse (a UTC day with no activity never gets a row) and
 * keyed by UTC calendar day. Controller ruling: fill every missing day
 * between the first and last key -- and up to `windowEnd` when it is known --
 * with a zero point, so a gap in the data never silently joins two distant
 * days on the chart. A day with no committed effects (present with all
 * zeros, or missing entirely) gets `pct: null`: an honest gap marker, never a
 * misleading 0% day.
 */
export const toDailyPoints = (daily: CafDailyEffects[], windowEnd?: string): DailyPoint[] => {
  if (!daily.length) return [];
  const byDate = new Map(daily.map((day) => [day.date, day]));
  const keys = Array.from(byDate.keys());
  const first = keys.reduce((min, day) => (day < min ? day : min), keys[0]);
  let last = keys.reduce((max, day) => (day > max ? day : max), keys[0]);
  if (windowEnd && windowEnd > last) last = windowEnd;
  return utcDayRange(first, last).map((date) => {
    const day = byDate.get(date);
    const handled = day ? day.completed + day.archived : 0;
    const total = day ? handled + day.held + (day.assigned ?? 0) : 0;
    return { date, handled, total, pct: total > 0 ? percent(handled, total) : null };
  });
};

/**
 * Controller note 3: the daily line's trailing empty days should fill up to
 * the day before the snapshot's window ends (a day only partially inside the
 * window is never treated as fully covered). Pure UTC arithmetic -- parses
 * the ISO instant and steps back exactly one day -- so a BST instant never
 * shifts under local getters. `null` when the value can't be parsed.
 */
export const lastIncludedUtcDay = (windowEndExclusive: string): string | null => {
  const ms = Date.parse(windowEndExclusive);
  return Number.isNaN(ms) ? null : utcMsToDay(ms - ONE_DAY_MS);
};

export const toReasonRows = (holdReasons: Record<string, number>): ReasonRow[] => {
  const counts: Partial<Record<HoldGroupKey, number>> = {};
  Object.keys(holdReasons).forEach((code) => {
    const key = holdGroup(code);
    counts[key] = (counts[key] ?? 0) + (holdReasons[code] || 0);
  });
  return HOLD_GROUPS
    .map((group) => ({ key: group.key, label: group.label, count: counts[group.key] ?? 0 }))
    .filter((row) => row.count > 0)
    .sort((a, b) => (Number(a.key === 'other') - Number(b.key === 'other')) || (b.count - a.count));
};

export const toGapRows = (
  coverage: CafDashboardCoverage,
  lenderOf: (sender: string) => string | null,
  limit = 5,
): GapRow[] => {
  const labels = new Map((coverage.lender_groups ?? []).map((group) => [group.lender_key, group.lender_label] as [string, string]));
  return (coverage.sender_groups ?? [])
    .filter((group) => Boolean(group.sender) && group.searchable_examples === 0 && group.incoming > 0)
    .sort((a, b) => b.incoming - a.incoming)
    .slice(0, limit)
    .map((group) => ({
      sender: group.sender as string,
      lender: (group.lender_key && labels.get(group.lender_key)) || lenderOf(group.sender as string),
      count: group.incoming,
      existingExamples: group.existing_examples ?? null,
      reason: group.existing_examples == null ? 'coverage_unknown'
        : group.existing_examples === 0 ? 'no_examples'
        : group.approval_pending_examples ? 'approval_pending'
        : group.approved_missing_vector ? 'missing_vector'
        : group.switched_off_examples ? 'switched_off' : 'needs_review',
    }));
};
